'use client'

import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {cx} from '@local/utils/cx'
import {ElementOrSelector, animate, spring} from 'motion'
import {SanityImage} from '@local/ui/components'
import {SanityImageType} from '@local/types'

export interface ImagesStackProps {
	images: Array<SanityImageType>
	className?: string
	imageClassName?: string
	startPlaying?: boolean
}

export const ImageStack = forwardRef(
	(
		{
			images,
			className,
			imageClassName,
			startPlaying = false,
		}: ImagesStackProps,
		parentRef,
	) => {
		const containerRef = useRef(null)
		const setRefs = useCallback((node) => {
			containerRef.current = node
			if (parentRef) parentRef.current = node
		}, [])

		const itemsRef = useRef<Array<HTMLElement | null>>([])
		const secondItemsRef = useRef<Array<HTMLElement | null>>([])
		const [currentIndex, setCurrentIndex] = useState<number>(0)
		const prevIndex = useRef(-1)
		const zIndexTimeout = useRef()

		useEffect(() => {
			itemsRef.current = itemsRef.current.slice(0, images.length)
			secondItemsRef.current = secondItemsRef.current.slice(0, images.length)
		}, [images])

		useEffect(() => {
			if (secondItemsRef.current && startPlaying) {
				secondItemsRef.current.forEach((item, index) => {
					const isActive = index === currentIndex
					const isPrev = index === prevIndex.current

					const scalePrev = isPrev ? 1 : 0
					const scaleActive = isActive ? 1 : 0
					const stayActive = scaleActive || scalePrev

					animate(
						item as ElementOrSelector,
						{
							transform: [
								`translate3d(0px, 0px, 0) scale3d(${scalePrev},${scalePrev},${scalePrev})`,
								`translate3d(0px, 0px, 0) scale3d(${stayActive},${stayActive},${stayActive})`,
							],
						},
						{
							delay: 1.5,
							easing: spring({velocity: 0, stiffness: 50, damping: 400}),
						},
					)
				})

				zIndexTimeout.current = setTimeout(() => {
					secondItemsRef.current.forEach((item, index, {length}) => {
						if (item) item.style.zIndex = index === currentIndex ? length + 1 : length
					})
				}, 1610)

				prevIndex.current = currentIndex
			}

			return () => {
				clearTimeout(zIndexTimeout.current)
			}
		}, [startPlaying, currentIndex])

		useImperativeHandle(parentRef, () => {
			return {
				next: (index:number) => { setCurrentIndex(index) },
				getIndex: () => currentIndex,
				items: itemsRef.current,
			}
		}, [])

		return (
			<div
				ref={setRefs}
				className={cx(
					className,
					'relative h-full w-full',
					'flex justify-center items-center',
				)}>
				<div className={cx(
					'realtive aspect-[150/210] w-150 md:aspect-[251/375] md:w-251 lg-xs-h-max:w-220',
					'[&_figure]:absolute [&_figure]:will-change-transform',
					'[&_.stack-image]:rounded-[4px] lg:[&_.stack-image]:rounded-lg [&_.stack-image]:object-cover [&_.stack-image]:max-w-none',
					'[&_.stack-image]:aspect-[150/210] [&_.stack-image]:w-150',
					'md:[&_.stack-image]:aspect-[251/375] md:[&_.stack-image]:w-251 lg-xs-h-max:[&_.stack-image]:w-220',
					'[&_.figure-two]:scale-0',
				)}>
					{images.map((item, index) => (
						<figure
							key={index}
							ref={el => (itemsRef.current[index] = el)}
							style={{zIndex: images.length - 1 - index}}>
							<SanityImage
								image={item}
								alt={item.alt}
								width={150}
								height={210}
								mobileWidth={150}
								mobileOnly
								className={cx('stack-image', imageClassName)} />
							<SanityImage
								image={item}
								alt={item.alt}
								width={251}
								height={375}
								desktopWidth={251}
								desktopOnly
								className={cx('stack-image', imageClassName)} />
						</figure>
					))}
					{images.map((item, index) => (
						<figure
							key={`second-images-${index}`}
							ref={el => (secondItemsRef.current[index] = el)}
							className='figure-two'
							style={{zIndex: images.length - 1 - index}}>
							<SanityImage
								image={item}
								alt={item.alt}
								width={150}
								height={210}
								mobileWidth={150}
								mobileOnly
								className={cx('stack-image', imageClassName)} />
							<SanityImage
								image={item}
								alt={item.alt}
								width={251}
								height={375}
								desktopWidth={251}
								desktopOnly
								className={cx('stack-image', imageClassName)} />
						</figure>
					))}
				</div>
			</div>
		)
	},
)
